import React, { useCallback, useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import FormLabel from 'components/atoms/FormLabel';
import { useTranslations } from 'hooks/useTranslations';
import {
  ControlButton,
  ImageContainerStyled,
  ImagesGalleryContainer,
  ImageStyled,
} from './style';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import Icon from 'components/atoms/Icon/Icon';
import { EIconTypes } from 'constants/Icons';
import { ESize, IEntryData } from 'models/galery';
import { formatDate } from 'utils/date';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FormInput } from 'components/molecules/FormInput';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { colors } from 'theme/colors';
import { Alert, EAlertVariants } from 'components/atoms/Alert';

interface IImageContainer {
  id: string;
  data: IEntryData;
  image: string | File;
  index: number;
  handleFullSize: () => void;
  handleDelete: () => void;
  dateFormat: string;
  control: any;
  errors: any;
  setValue: any;
  withLinkedLicences: boolean;
  sizeImage: ESize;
  sizeFullImage: ESize;
  warnings?: string[];
}

export const ImageContainer = ({
  id,
  data,
  image,
  index,
  handleFullSize,
  handleDelete,
  dateFormat,
  control,
  errors,
  setValue,
  withLinkedLicences,
  sizeImage,
  warnings = [],
}: IImageContainer) => {
  const { t } = useTranslations();
  const { id: ownerId = '' } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [imgSrc, setimgSrc] = useState<string>('');
  const type = searchParams.get('type');
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const main = Number(data.position) === 0;

  const canDelete = data.licences && data.licences.length ? false : true;

  useEffect(() => {
    if (image) {
      if (typeof image === 'string') {
        setimgSrc(image);
      } else {
        setimgSrc(URL.createObjectURL(image));
      }
    }
  }, [image]);

  return (
    <ImageContainerStyled
      container
      main={main}
      ref={setNodeRef}
      style={style}
      sizeImage={sizeImage}
    >
      <Grid
        container
        size={{ xs: 12 }}
        sx={{ height: '32px', py: '4px', justifyContent: 'space-between' }}
      >
        <Grid container sx={{ flexWrap: 'nowrap', gap: '4px' }}>
          <ControlButton {...attributes} {...listeners}>
            <Icon type={EIconTypes.draggable} fontSize={16} />
          </ControlButton>
          {main && (
            <Chips text={t('images.mainPhoto.label')} variant={EChipsVariants.primary} />
          )}
        </Grid>
        <Grid container sx={{ flexWrap: 'nowrap', gap: '2px', justifyContent: 'right' }}>
          <ControlButton onClick={handleFullSize}>
            <Icon type={EIconTypes.fullScreen} fontSize={16} />
          </ControlButton>
          {canDelete && (
            <ControlButton onClick={handleDelete}>
              <Icon type={EIconTypes.delete} fontSize={16} />
            </ControlButton>
          )}
        </Grid>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <ImagesGalleryContainer sizeImage={sizeImage}>
          <ImageStyled>
            <img src={imgSrc} alt={data.filename} />
          </ImageStyled>
        </ImagesGalleryContainer>
      </Grid>
      <Grid sx={{ p: '12px' }}>
        <Grid size={{ xs: 12 }}>
          <FormInput
            name={`entryParameters.${index}.filename`}
            label={t('manageImages.image.fileName.label')}
            control={control}
            errors={errors}
            setValue={setValue}
            required
          />
        </Grid>
        {data.createdDate && (
          <Grid sx={{ mb: '12px' }}>
            <FormLabel
              name={`entryParameters.${index}.createDate`}
              label={t('manageImages.image.createDate.label')}
            />
            <Typography variant="body2">{formatDate(data.createdDate, dateFormat)}</Typography>
          </Grid>
        )}
        {!!withLinkedLicences && (
          <Grid size={{ xs: 12 }}>
            <FormLabel
              name={`entryParameters.${index}.linkedLicence`}
              label={t('manageImages.image.linkedLicence.label')}
            />
            {data.licences?.length ? (
              data.licences.map((licence) => (
                <Link
                  to={`${ERouteLinks.LegalEntityViewLicence.replace(':id', ownerId).replace(
                    ':licenceId',
                    licence.licenceUniqueId,
                  )}?type=${type}&from=le`}
                  style={{ color: colors.primary600, textDecoration: 'none' }}
                >
                  <Typography variant="body2" sx={{ color: colors.primary600 }}>
                    {licence.licenceNumber}
                  </Typography>
                </Link>
              ))
            ) : (
              <Typography variant="body2">-</Typography>
            )}
          </Grid>
        )}
      </Grid>
      {!!warnings.length && (
        <Grid
          size={{ xs: 12 }}
          sx={{
            mt: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          {warnings.map((warning) => (
            <Alert key={warning} small variant={EAlertVariants.warning} text={t(warning)} />
          ))}
        </Grid>
      )}
    </ImageContainerStyled>
  );
};
